import React, { ReactNode, useState } from "react";
import styled from "styled-components";

import { Maybe } from "@graphql-types";
import { AbsoluteWrapper, Container } from "@util/standard";
import { colors, mediaQuery } from "@util/constants";
import { Color } from "@util/types";
import { useEventListener } from "@util/hooks";
import { checkClickTarget } from "@util/helper";
import { IconDownChevron } from "@util/assets";

interface Props {
  onChange?: (string: string) => void;
  title?: Maybe<string> | undefined;
  data?: (Maybe<string> | undefined)[];
  children?: ReactNode;
  margin?: string;
}

const TitleContainer = styled(Container)<{ hoverColor?: Color | undefined }>`
  width: 100%;
  padding: 14px 35px;
  justify-content: space-between;
  background: transparent;
  &:hover {
    ${({ hoverColor }) => hoverColor && `background: ${colors[hoverColor]};`};
  }
  transition: all 0.3s;
`;

const Title = styled.p<{ margin?: string }>`
  font-size: 16px;
  margin: 0;

  ${({ margin }) => margin && `margin:${margin};`};
  opacity: 1;
  &:hover {
    opacity: 0.5;
  }
  ${mediaQuery.largeDown} {
    margin-right: 25px;
  }
`;

const DropDownOpenWrapper = styled(AbsoluteWrapper)`
  top: 100%;
  width: 100%;
  max-height: 270px;
  overflow-y: auto;
  z-index: 2;

  ::-webkit-scrollbar {
    background: ${colors.lightGrey};
  }
  ::-webkit-scrollbar {
    width: 20px;
  }
  ::-webkit-scrollbar-thumb {
    border: 8px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
    border-radius: 9999px;
    background-color: ${colors.charcoal};
  }
`;

const ArrowWrapper = styled.div<{ open: boolean }>`
  margin: auto 0;
  ${({ open }) => (open ? `transform:rotateZ(180deg);` : `transform:rotateZ(0deg);`)};
  transition: all 0.2s;
`;

function Dropdown({ onChange, title, data, children, margin }: Props) {
  const [dropDownOpen, setDropdownOpen] = useState(false);
  const [currentValue, setCurrentValue] = useState(title ?? "Select");

  const handleChange = (string: Maybe<string> | undefined) => () => {
    if (string == null) return;
    if (onChange) {
      onChange(string);
    }
    setCurrentValue(string);
    setDropdownOpen(false);
    return;
  };

  const handleClick = (e: Event) => {
    if (e && dropDownOpen) {
      checkClickTarget(
        e,
        "dropdown",
        () => null,
        () => setDropdownOpen(false),
      );
    }
  };

  useEventListener("click", handleClick);

  return (
    <Container
      backgroundColor="lightGrey"
      margin={margin ?? "auto"}
      width="100%"
      flexDirection="column"
      cursor="pointer"
      position="relative"
      className="dropdown"
    >
      <TitleContainer onClick={() => setDropdownOpen(!dropDownOpen)} className="dropdown">
        <Title>{currentValue}</Title>
        <ArrowWrapper open={dropDownOpen}>
          <IconDownChevron />
        </ArrowWrapper>
      </TitleContainer>
      {dropDownOpen && (
        <DropDownOpenWrapper className="dropdown">
          <Container
            flexDirection="column"
            padding="14px 35px"
            cursor="pointer"
            backgroundColor="lightGrey"
            width="100%"
            className="dropdown"
          >
            {!children && data && (
              <>
                {data.map((item, index) => {
                  if (item == null) return null;

                  const isLast = index === data.length - 1;

                  return (
                    <Title
                      margin={isLast ? "0" : "0 0 20px 0"}
                      onClick={handleChange(item)}
                      key={item + index}
                    >
                      {item}
                    </Title>
                  );
                })}
              </>
            )}
            {children}
          </Container>
        </DropDownOpenWrapper>
      )}
    </Container>
  );
}

export default Dropdown;
